import React, { forwardRef, InputHTMLAttributes, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { Field, controlClasses, controlSizeClasses, ControlSize, useField } from '../field';

export type InputSize = ControlSize;

interface IProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size: InputSize;
  /** Shown under the field while there is no error. */
  hint?: string;
  label?: string;
  /** Layout around the control (margins, width, grid placement) — on the `Field`, or on the bare control's
   * wrapper when there is no label. `className` styles the input itself; a margin there shifts the icon. */
  classNameWrap?: string;
  error?: string;
  showError?: boolean;
  showRequired?: boolean;
  icon?: {
    type: 'prefix' | 'suffix';
    icon: React.ReactNode;
  } | null;
}

/** The text keeps clear of the icon; the icon sits at the control's own side padding. */
const iconSlot: Record<InputSize, Record<'prefix' | 'suffix', { padding: string; position: string }>> = {
  sm: { prefix: { padding: 'pl-9', position: 'left-3' }, suffix: { padding: 'pr-9', position: 'right-3' } },
  md: { prefix: { padding: 'pl-11', position: 'left-4' }, suffix: { padding: 'pr-11', position: 'right-4' } },
  lg: { prefix: { padding: 'pl-11', position: 'left-4' }, suffix: { padding: 'pr-11', position: 'right-4' } },
};

// The same slot in pixels: its inset from the edge, and the padding the classes above give a 20px icon.
const slotInset: Record<InputSize, number> = { sm: 12, md: 16, lg: 16 };
const slotPadding: Record<InputSize, number> = { sm: 36, md: 44, lg: 44 };
const SLOT_GAP = 8;

type SizedControlProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size: InputSize;
  icon: IProps['icon'];
  wrapClassName?: string;
};

const SizedControl = forwardRef<HTMLInputElement, SizedControlProps>(
  ({ size, icon, wrapClassName, className, style, ...rest }, ref) => {
    const field = useField();
    // A slot wider than an icon (a currency mark and «Макс») widens the padding to fit, so text never runs under it.
    const slotRef = useRef<HTMLSpanElement>(null);
    const [slotWidth, setSlotWidth] = useState(0);
    useLayoutEffect(() => {
      const slot = slotRef.current;
      if (!slot) return setSlotWidth(0);
      const measure = () => setSlotWidth(slot.offsetWidth);
      measure();
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(measure);
      observer.observe(slot);
      return () => observer.disconnect();
    }, [icon?.type]);
    const needed = slotInset[size] + slotWidth + SLOT_GAP;
    const widened =
      icon && needed > slotPadding[size]
        ? { [icon.type === 'suffix' ? 'paddingRight' : 'paddingLeft']: needed }
        : undefined;
    const input = (
      <input
        ref={ref}
        id={field?.id}
        aria-invalid={field?.invalid || undefined}
        aria-describedby={field?.describedBy}
        className={classNames(
          controlClasses(Boolean(field?.invalid)),
          controlSizeClasses[size],
          icon && iconSlot[size][icon.type].padding,
          className,
        )}
        style={widened ? { ...style, ...widened } : style}
        {...rest}
      />
    );

    // The wrapper stays even without an icon: when `icon` toggles while typing (a select with no matches),
    // a changing tree would remount the <input> and drop focus.
    return (
      <div className={classNames('relative w-full', wrapClassName)}>
        {input}
        {icon && (
          <span
            ref={slotRef}
            className={classNames(
              'absolute top-1/2 flex -translate-y-1/2 items-center text-ink-muted',
              iconSlot[size][icon.type].position,
            )}
          >
            {icon.icon}
          </span>
        )}
      </div>
    );
  },
);
SizedControl.displayName = 'InputControl';

export const Input = forwardRef<HTMLInputElement, IProps>(
  (
    {
      size,
      hint,
      label = '',
      type = 'text',
      className = '',
      classNameWrap = '',
      required,
      error = '',
      showError = true,
      showRequired = false,
      icon = null,
      ...restProps
    },
    ref,
  ) => {
    const bare = !label && !hint && !error;
    const control = (
      <SizedControl
        ref={ref}
        size={size}
        icon={icon}
        wrapClassName={bare ? classNameWrap : undefined}
        type={type}
        className={className}
        required={required}
        {...restProps}
      />
    );
    if (bare) return control;

    return (
      <Field
        size={size}
        label={label}
        hint={hint}
        error={showError ? error : undefined}
        invalid={Boolean(error)}
        required={required}
        requiredHint={showRequired}
        className={classNameWrap}
      >
        {control}
      </Field>
    );
  },
);
Input.displayName = 'Input';
